import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { safeAnchorHref } from '../../../lib/link';
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraStatusDot } from '../ensigns/status-dot';
import { SpecraHeading, SpecraText } from '../ensigns/typography';
import { SpecraTooltip } from './tooltip';
const sparkToneClass = {
    neutral: 'text-muted-foreground',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    info: 'text-info'
};
/** A metric's history as a quiet line, its last point marked. Decorative: the value says it. */ function MetricSparkline({ tone, values }) {
    const known = values.filter((v)=>v !== null);
    if (known.length < 2) return null;
    const min = Math.min(...known);
    const max = Math.max(...known);
    const span = max - min || 1;
    const w = 100;
    const h = 24;
    const step = values.length > 1 ? w / (values.length - 1) : w;
    const pts = values.map((v, i)=>v === null ? null : [
            i * step,
            h - 2 - (v - min) / span * (h - 4)
        ]).filter((p)=>p !== null);
    const last = pts[pts.length - 1];
    return /*#__PURE__*/ _jsxs("svg", {
        "aria-hidden": "true",
        className: cn('mt-[var(--space-2)] block h-6 w-full', sparkToneClass[tone]),
        "data-metric-sparkline": "",
        preserveAspectRatio: "none",
        viewBox: `0 0 ${w} ${h}`,
        children: [
            /*#__PURE__*/ _jsx("polyline", {
                fill: "none",
                points: pts.map((p)=>p.join(',')).join(' '),
                stroke: "currentColor",
                strokeWidth: "1.5",
                vectorEffect: "non-scaling-stroke"
            }),
            /*#__PURE__*/ _jsx("circle", {
                cx: last[0],
                cy: last[1],
                fill: "currentColor",
                r: "2",
                vectorEffect: "non-scaling-stroke"
            })
        ]
    });
}
const metricToneClass = {
    neutral: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    info: 'text-info'
};
/** One KPI value with semantic state, label, and context. */ export function SpecraMetricItem({ className, density = 'default', loading = false, metric, renderLink, ...props }) {
    const fallbackHref = metric.href ? safeAnchorHref(metric.href) : undefined;
    // A loading tile is inert: never a link, and never a button that could submit a form.
    const Component = loading ? 'div' : fallbackHref ? 'a' : metric.onClick ? 'button' : 'div';
    const interactiveProps = fallbackHref ? {
        href: fallbackHref
    } : metric.onClick ? {
        onClick: metric.onClick,
        type: 'button'
    } : {};
    const interactive = !loading && Boolean(metric.href || metric.onClick);
    const tone = metric.tone ?? 'neutral';
    const itemClassName = cn('block min-w-0 px-[var(--space-5)] py-[var(--space-4)] text-left', interactive && `cursor-pointer hover:bg-accent/50 ${specraFocusRing}`, className);
    const body = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-[var(--space-kpi-gap)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraStatusDot, {
                        tone: tone
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "text-[length:var(--fs-ui-label)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)]",
                        size: "micro",
                        tone: "muted",
                        children: metric.label
                    }),
                    metric.info ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                        className: "max-w-64",
                        content: metric.info,
                        delayDuration: 150,
                        children: /*#__PURE__*/ _jsx("span", {
                            "aria-label": `About ${metric.label}`,
                            className: cn('inline-flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center rounded-full border border-muted-foreground/50 text-[length:var(--fs-ui-micro)] font-bold leading-none text-muted-foreground', specraFocusRing),
                            "data-metric-info": "",
                            // Opening the tip must not follow the tile's link.
                            onClick: (e)=>{
                                e.preventDefault();
                                e.stopPropagation();
                            },
                            onKeyDown: (e)=>{
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            },
                            role: "button",
                            tabIndex: 0,
                            children: "i"
                        })
                    }) : null
                ]
            }),
            metric.context ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                className: "mt-[var(--space-1)]",
                size: "meta",
                tone: "muted",
                children: metric.context
            }) : null,
            loading ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": "true",
                        className: "mt-[var(--space-2)] h-[var(--fs-h3)] w-16 animate-pulse rounded-[var(--radius-sm)] bg-muted"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "sr-only",
                        children: "Loading"
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraHeading, {
                className: cn('mt-[var(--space-2)] [overflow-wrap:anywhere]', metric.muted ? 'text-muted-foreground' : metricToneClass[tone]),
                // A muted value is words ("No data yet"), so it reads at section size.
                size: metric.muted ? 'sm' : density === 'compact' ? 'md' : 'lg',
                children: metric.value
            }),
            !loading && metric.trend ? /*#__PURE__*/ _jsx(MetricSparkline, {
                tone: tone,
                values: metric.trend
            }) : null,
            metric.note ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                className: "mt-[var(--space-1-5)] text-[color:var(--text-faint)]",
                size: "meta",
                tone: "muted",
                children: metric.note
            }) : null,
            metric.action ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                className: "mt-[var(--space-2)]",
                size: "meta",
                tone: "info",
                children: metric.action
            }) : null
        ]
    });
    if (!loading && metric.href && renderLink) {
        return renderLink({
            ...props,
            children: body,
            className: itemClassName,
            href: metric.href
        });
    }
    return /*#__PURE__*/ _jsx(Component, {
        className: itemClassName,
        ...loading ? {} : interactiveProps,
        ...props,
        children: body
    });
}
